<template>
  <div style="min-height: 100vh">
    <!-- Purpose Section -->
    <section class="slogan hero">
      <v-container>
        <h1 class="pa-16">Independent Educational Assessment</h1>
        <h3 class="pa-16">
          A professional solution designed to enhance educational outcomes for all South African learners
        </h3>
      </v-container>
    </section>

    <!-- Compliance Sectino -->
    <section class="trust-section text-section">
      <v-container class="pb-16">
        <h1 class="pa-8">
          Working Together Towards Education Excellence
        </h1>
        <p>
          <strong>Our platform serves as a bridge between families and educational requirements.</strong> We work in partnership with the Department of Basic Education to provide quality assessment services that benefit everyone.
        </p>
        <p>
          By participating in our assessment program, families contribute to valuable educational statistics while receiving professional insights about their children's learning progress. This collaborative approach helps improve education across South Africa while maintaining complete privacy for all participants.
        </p>
        <v-row class="align-stretch pt-8">
          <v-col v-for="purpose in purposes" :key="purpose.title" cols="12" md="6" xl="3">
            <v-card height="100%" class="pa-4 text-center">
              <v-card-title>
                <div class="mb-4">
                  <v-icon color="success" size="56">{{ purpose.icon }}</v-icon>
                </div>
                <h3>{{ purpose.title }}</h3>
              </v-card-title>
              <v-card-text>
                {{ purpose.description }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>      
        <h2 class="pt-8">Trusted by South African Families</h2>
        <v-row class="align-stretch pt-4">
          <v-col v-for="benefit in parentBenefits" :key="benefit.title" cols="12" sm="6" lg="4">
            <v-card height="100%" class="pa-4 text-center">
              <v-card-title>
                <div class="mb-4">
                  <v-icon color="primary" size="56">{{ benefit.icon }}</v-icon>
                </div>
                <h3>{{ benefit.title }}</h3>
              </v-card-title>
              <v-card-text>
                {{ benefit.description }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits for Schools Section -->
    <section class="schools-benefits-section hero">
      <v-container class="pb-8">
        <h1 class="pa-8">Empowering School Assessment</h1>
        <v-row class="align-stretch pt-4 pb-16">
          <v-col v-for="benefit in schoolBenefits" :key="benefit.title" cols="12" sm="6" lg="4">
            <v-card height="100%" class="text-center semi-transparent" elevation="4">
              <v-card-title>
                <div class="mb-4">
                  <v-icon color="success" size="56">{{ benefit.icon }}</v-icon>
                </div>
                <h2>{{ benefit.title }}</h2>
              </v-card-title>
              <v-card-text>
                {{ benefit.description }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- How It Works Section -->
    <section class="how-it-works text-section">
      <v-container>
        <h1 class="pa-8">
          Simple Path to Educational Excellence
        </h1>
        <h4>
          Four straightforward steps to professional assessment
        </h4>
        <v-row justify="center" class="align-stretch pt-8">
          <v-col 
            v-for="(step, index) in steps" 
            :key="step.number" 
            cols="12" 
            sm="3" 
            class="position-relative pl-8 pr-8"
          >
            <div class="mb-4 d-flex justify-center">
              <v-avatar size="64" :color="step.color">
                <span class="text-h4 font-weight-bold text-white">{{ step.number }}</span>
              </v-avatar>
            </div>
            <h4>{{ step.title }}</h4>
            <p class="text-justify">{{ step.description }}</p>
            
            <!-- Arrow (only show for non-last steps) -->
            <template v-if="index < steps.length - 1">
              <v-icon 
                size="32" 
                color="primary" 
                class="arrow-right d-none d-sm-flex position-absolute"
                style="right: -16px; top: 50%; transform: translateY(-50%);"
              >
                mdi-arrow-right
              </v-icon>
              <v-icon 
                size="32" 
                color="primary" 
                class="arrow-down d-flex d-sm-none position-absolute"
                style="bottom: -20px; left: 50%; transform: translateX(-50%);"
              >
                mdi-arrow-down
              </v-icon>
            </template>
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col cols="12" sm="8" md="6" lg="5" xl="3">
            <v-card color="surface">
              <v-card-text class="text-center">
                <v-btn
                  color="success"
                  size="large"
                  variant="flat"
                  class="px-8 py-3"
                  to="/demo"
                >
                <v-icon start>mdi-play-circle</v-icon>
                  Try a FREE assessment
                </v-btn>
                <v-divider class="my-4" />
                <div>
                  No credit card needed • No registration • No commitment
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <div class="d-flex justify-center ga-4 flex-wrap mb-6">
              <v-btn
                  color="info"
                  size="x-large"
                  class="px-6 py-3"
                  to="/faqs"
                >
                <v-icon start>mdi-message-text</v-icon>
                  Get More Information
                </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>
  </div>
</template>

<script setup lang="ts">
import { UserRole } from '#shared/enums';
import { nextTick } from 'vue';

// Check if user is already authenticated and redirect appropriately
const auth = useAuth();

// Use nextTick to avoid hydration mismatch - delay the auth check until after hydration
onMounted(() => {
  nextTick(() => {
    if (auth.isAuthenticated()) {
      const userRole = auth.getRole();
      
      let redirectPath = '/login';
      
      switch (userRole) {
        case UserRole.ADMIN:
          redirectPath = '/administration';
          break;
        case UserRole.DEMO:
          redirectPath = '/assessments';
          break;
        case UserRole.INSPECTOR:
          redirectPath = '/statistics';
          break;
        case UserRole.PARENT:
          redirectPath = '/report';
          break;
        case UserRole.SUPERVISOR:
          redirectPath = '/centre';
          break;
        case UserRole.STUDENT:
          redirectPath = '/assessments';
          break;
        case UserRole.TESTER:
          redirectPath = '/drafts';
          break;
        default:
          redirectPath = '/login';
      }
      
      navigateTo(redirectPath);
    }
  });
});

// SEO optimization
useHead({
  title: 'Independant Assessor - INDA Platform',
  meta: [
    {
      name: 'description',
      content: 'Quality educational assessments for homeschool, public, and private schools. Meet government requirements while gaining valuable insights into learning progress. Professional, secure, affordable - trusted by SA families.'
    },
    {
      name: 'keywords',
      content: 'educational assessment South Africa, CAPS curriculum testing, professional school evaluation, learning progress measurement, government compliance education, quality assessment platform'
    }
  ]
});

const purposes = ref([
  {
    title: 'Compliance',
    description: "Generate official, verifiable reports that satisfy government requirements while protecting your family's rights and demonstrating your commitment to your child's education.",
    icon: 'mdi-shield-check',
  },
  {
    title: 'Inter Curricular',
    description: "Provides CAPS-aligned assessments for government reporting while respecting your educational choices.",
    icon: 'mdi-book-education',
  },
  {
    title: 'Homeschool Friendly',
    description: "Continue with your preferred homeschooling curriculum knowing you can demonstrate compliance without compromising your educational philosophy.",
    icon: 'mdi-home-heart',
  },
  {
    title: 'Support Public Schools',
    description: "Seamlessly aligns with public school requirements, reducing administrative burden and costs while providing standardized assessment support that schools can trust.",
    icon: 'mdi-school',
  }
]);

const parentBenefits = ref([
  {
    title: 'Trustworthy',
    description: "Your data is encrypted and securely stored with strict privacy controls. We never share personal information and comply with all South African data protection laws.",
    icon: 'mdi-lock',
  },  
  {
    title: 'Easy',
    description: "Child-friendly interface designed for young learners with simple navigation, clear instructions, and stress-free assessment experience that builds confidence.",
    icon: 'mdi-emoticon-happy-outline',
  },
  {
    title: 'Local',
    description: "Extensive network of certified assessment centers across South Africa, making quality education assessment accessible in your community.",
    icon: 'mdi-map-marker-multiple',
  },
  {
    title: 'Multilingual',
    description: "Assessments available in all 11 official South African languages, ensuring every child can demonstrate their abilities in their preferred language without barriers.",
    icon: 'mdi-account-group-outline',
  },  
  {
    title: 'Affordable',
    description: "Every South African family deserves access to professional evaluation services with transparent pricing and no hidden fees.",
    icon: 'mdi-cash-multiple',
  },
  {
    title: 'South African',
    description: 'Developed by local educators who understand our unique educational landscape, cultural context, and the specific needs of South African families.',
    icon: 'mdi-flag',
  }
]);

const schoolBenefits = ref([
  {
    title: 'Additional Revenue',
    description: 'Transform your computer labs into certified assessment centres, generating additional income while serving your community with existing infrastructure.',
    icon: 'mdi-cash-check',
  },
  {
    title: 'Reduce Workload',
    description: 'Streamline assessment processes with automated administration, grading, and reporting, freeing teachers to focus on instruction and student support.',
    icon: 'mdi-briefcase-check',
  },
  {
    title: 'Professional Reports',
    description: 'Generate comprehensive, standardized reports that meet Department of Education requirements while providing actionable insights for educational planning.',
    icon: 'mdi-file-chart',
  },
  {
    title: 'Cost Effective',
    description: 'Eliminate printing costs, reduce administrative overhead, and minimize resource requirements with our digital assessment platform.',
    icon: 'mdi-piggy-bank',
  },
  {
    title: 'Device Compatible',
    description: 'Works on existing school computers and tablets without expensive upgrades. Optimized for older devices and slower internet connections.',
    icon: 'mdi-devices',
  },
  {
    title: 'Training & Support',
    description: 'Comprehensive training for educators and ongoing technical support ensure smooth implementation and maximum benefit from the platform.',
    icon: 'mdi-lifebuoy',
  }
]);

const steps = ref([
  {
    number: 1,
    title: 'Register',
    description: 'Create your family account by providing basic information. Our simple registration process respects your privacy while ensuring secure access.',
    color: 'error',
  },
  {
    number: 2,
    title: 'Book Session',
    description: 'Schedule an assessment appointment at your nearest certified assessment center.',
    color: 'warning',
  },
  {
    number: 3,
    title: 'Complete Assessment',
    description: 'Learners complete age-appropriate assessments at the certified assessment center with professional supervision.',
    color: 'info',
  },
  {
    number: 4,
    title: 'Receive Report',
    description: 'Get detailed, professional reports that document learning progress and provide valuable educational insights.',
    color: 'success',
  }
]);


</script>

<style scoped>
h1, h2, h3, h4 {
  text-align: center;
}

h1 {
  font-size: 2.4rem;
}

h2 {
  font-size: 2.1rem;
}

h3 {
  font-size: 1.8rem;
}

h4 {
  font-size: 1.5rem;
}

p {
  margin: 1rem 0;
}

@media (max-width: 960px) {
  h1 {
    font-size: 2rem;
  }
  
  h2 {
    font-size: 1.75rem;
  }
  
  h3 {
    font-size: 1.5rem;
  }

  h4 {
    font-size: 1.25rem;
  }
}

@media (max-width: 600px) {
  h1 {
    font-size: 1.9rem;
  }
  
  h2 {
    font-size: 1.6rem;
  }
  
  h3 {
    font-size: 1.3rem;
  }

  h4 {
    font-size: 1.15rem;
  }

  /* p {
    font-size: 1.125rem;
    margin: 1rem 0;
  } */
}

.hero {
  margin-left: calc(-50vw + 50%);
  margin-right: calc(-50vw + 50%);
  width: 100vw;
  position: relative;
  background: linear-gradient(135deg, rgb(var(--v-theme-selectable)), rgb(var(--v-theme-background)));
}

.text-section {
    position: relative;
    background-color: rgb(var(--v-theme-background));
    /* Vuetify elevation-4 equivalent */
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.2), 
                0px 4px 5px 0px rgba(0, 0, 0, 0.14), 
                0px 1px 10px 0px rgba(0, 0, 0, 0.12);
    z-index: 1;
}


.slogan {
  background-image: url('~/assets/images/slogan.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.schools-benefits-section {
  background-image: url('~/assets/images/benefits.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgb(var(--v-theme-background));
  opacity: 0.5;
  pointer-events: none;
}

.hero .v-container {
  position: relative;
  z-index: 1;
}

/* Dark theme styles */
.v-theme--dark .hero h1, 
.v-theme--dark .hero h2, 
.v-theme--dark .hero h3, 
.v-theme--dark .hero h4,
.v-theme--dark .hero .v-card-text {
  color: white;
  text-shadow: 1px 1px 0 black, 
              -1px -1px 0 black, 
              1px -1px 0 black, 
              -1px 1px 0 black;
}

/* Light theme styles */
.v-theme--light .hero h1, 
.v-theme--light .hero h2, 
.v-theme--light .hero h3, 
.v-theme--light .hero h4,
.v-theme--light .hero .v-card-text {
  color: black;
  text-shadow: 1px 1px 0 white, 
              -1px -1px 0 white, 
              1px -1px 0 white, 
              -1px 1px 0 white;
}

section {
  overflow-x: hidden;
}

.text-justify {
  text-align: justify;
}

.semi-transparent {
  background-color: rgba(var(--v-theme-surface), 0.5) !important;
  backdrop-filter: blur(3px);
}
</style>
